import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Search, Plus, Download, Upload, ChevronDown, X, AlertTriangle, DollarSign, Users, TrendingUp, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) => {
  if (!v) return '0';
  const n = Number(v);
  if (n >= 10000000) return `${(n/10000000).toFixed(2).replace(/\.?0+$/,'')}Cr`;
  if (n >= 100000) return `${(n/100000).toFixed(1).replace(/\.?0+$/,'')}L`;
  if (n >= 1000) return `${(n/1000).toFixed(1).replace(/\.?0+$/,'')}K`;
  return n.toLocaleString('en-IN');
};

export default function VendorModule() {
  const { user } = useAuth();
  const [tab, setTab] = useState('master');
  const tabs = [
    { id: 'master', label: 'Vendor Master', icon: Users },
    { id: 'payables', label: 'Payables', icon: FileText },
    { id: 'aging', label: 'Aging Dashboard', icon: AlertTriangle },
    { id: 'cashflow', label: 'Cash Flow', icon: TrendingUp },
  ];

  return (
    <div className="space-y-4" data-testid="vendor-module">
      <div>
        <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Vendor & Payables</h2>
        <p className="text-xs" style={{ color: '#9CA3AF' }}>Vendor master, payables tracking, aging analysis, cash flow</p>
      </div>
      <div className="flex gap-1 border-b" style={{ borderColor: '#E5E1DC' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-all ${tab === t.id ? 'border-[#2A7B88] text-[#2A7B88]' : 'border-transparent text-[#9CA3AF] hover:text-[#6B7280]'}`}
            data-testid={`vendor-tab-${t.id}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'master' && <VendorMaster />}
      {tab === 'payables' && <PayablesTracker />}
      {tab === 'aging' && <AgingDashboard />}
      {tab === 'cashflow' && <CashFlowView />}
    </div>
  );
}

function VendorMaster() {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [form, setForm] = useState({});

  const fetch = useCallback((q = '', city = '') => {
    let url = `/vendors?search=${q}&limit=500`;
    if (city) url += `&city=${city}`;
    api.get(url).then(r => { setVendors(r.data.vendors); setTotal(r.data.total); }).catch(() => {});
  }, []);

  useEffect(() => { fetch(search, cityFilter); }, [search, cityFilter, fetch]);

  const cities = [...new Set(vendors.map(v => v.city).filter(Boolean))].sort();

  const save = async () => {
    try {
      if (editVendor) {
        await api.put(`/vendors/${editVendor.id}`, form);
      } else {
        await api.post('/vendors', form);
      }
      setShowForm(false); setEditVendor(null); setForm({});
      fetch(search, cityFilter);
    } catch {}
  };

  const del = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await api.delete(`/vendors/${id}`); fetch(search, cityFilter); } catch {}
  };

  const startEdit = (v) => {
    setEditVendor(v);
    setForm({ name: v.name, contact_person: v.contact_person, city: v.city, state: v.state, gstin: v.gstin, email: v.email, mobile: v.mobile, credit_limit: v.credit_limit, category: v.category, payment_terms_days: v.payment_terms_days, is_supplier: v.is_supplier, is_city_ledger: v.is_city_ledger });
    setShowForm(true);
  };

  return (
    <div className="space-y-3" data-testid="vendor-master">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Vendors ({total})</p>
        <div className="flex gap-2">
          <a href={`${API_URL}/api/vendors/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="export-vendors">
            <Download size={12} /> Export
          </a>
          <button onClick={() => { setShowForm(true); setEditVendor(null); setForm({}); }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-vendor-btn">
            <Plus size={12} /> Add Vendor
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-2.5 text-[#9CA3AF]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border bg-white" style={{ borderColor: '#E5E1DC' }} data-testid="vendor-search" />
        </div>
        <select value={cityFilter} onChange={e => setCityFilter(e.target.value)} className="px-2 py-2 text-xs rounded-lg border bg-white" style={{ borderColor: '#E5E1DC' }}>
          <option value="">All Cities</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {showForm && (
        <div className="p-4 rounded-lg border bg-white" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{editVendor ? 'Edit Vendor' : 'Add Vendor'}</h4>
            <button onClick={() => { setShowForm(false); setEditVendor(null); }}><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'name', label: 'Vendor Name', required: true },
              { key: 'contact_person', label: 'Contact Person' },
              { key: 'city', label: 'City' },
              { key: 'state', label: 'State' },
              { key: 'gstin', label: 'GSTIN' },
              { key: 'email', label: 'Email' },
              { key: 'mobile', label: 'Mobile' },
              { key: 'credit_limit', label: 'Credit Limit', type: 'number' },
              { key: 'payment_terms_days', label: 'Payment Terms (days)', type: 'number' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>{f.label}</label>
                <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: e.target.value})}
                  className="w-full px-2 py-1.5 text-sm rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
              </div>
            ))}
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>Category</label>
              <select value={form.category || 'Other'} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full px-2 py-1.5 text-sm rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {["Vegetables","Fruits","Meat & Poultry","Fish & Seafood","Dairy","Dry Goods","Spices","Beverages","Liquor","Bakery","Packaging","Equipment","Housekeeping","Services","Utilities","Other"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: '#2A7B88' }} data-testid="save-vendor-btn">
              {editVendor ? 'Update' : 'Create'} Vendor
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-[12px]">
          <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Vendor</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Contact</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>City</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>GSTIN</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Category</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Terms</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Type</th>
            <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Actions</th>
          </tr></thead>
          <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {vendors.map(v => (
              <tr key={v.id} className="hover:bg-[#FAFAF8]">
                <td className="px-3 py-2">
                  <p className="font-medium" style={{ color: '#1B2A4A' }}>{v.name}</p>
                  {v.mobile && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{v.mobile}</p>}
                </td>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{v.contact_person}</td>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{v.city}</td>
                <td className="px-3 py-2" style={{ color: '#9CA3AF' }}>{v.gstin || '—'}</td>
                <td className="px-3 py-2"><span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{v.category}</span></td>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{v.payment_terms_days}d</td>
                <td className="px-3 py-2">
                  {v.is_supplier && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 mr-1">Supplier</span>}
                  {v.is_city_ledger && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">City Ledger</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => startEdit(v)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2] mr-1" style={{ color: '#2A7B88' }}>Edit</button>
                  <button onClick={() => del(v.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayablesTracker() {
  const navigate = useNavigate();
  const [payables, setPayables] = useState([]);
  const [total, setTotal] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ status: 'Pending' });

  const fetch = useCallback(() => {
    let url = `/vendors/payables?limit=200`;
    if (statusFilter) url += `&status=${statusFilter}`;
    api.get(url).then(r => { setPayables(r.data.payables); setTotal(r.data.total); }).catch(() => {});
  }, [statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { api.get('/vendors?limit=500').then(r => setVendors(r.data.vendors)).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api.post('/vendors/payables', form);
      setShowForm(false); setForm({ status: 'Pending' }); fetch();
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      const update = { status };
      if (status === 'Paid') update.payment_date = new Date().toISOString().split('T')[0];
      await api.put(`/vendors/payables/${id}`, update);
      fetch();
    } catch {}
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/vendors/payables/${id}`); fetch(); } catch {}
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-3" data-testid="payables-tracker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Payables ({total})</p>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1.5 text-xs rounded-lg border bg-white" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All Status</option>
            {['Pending','Approved','Paid','Disputed','Overdue'].map(s => <option key={s}>{s}</option>)}
          </select>
          <a href={`${API_URL}/api/vendors/payables/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Download size={12} /> Export
          </a>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-payable-btn">
            <Plus size={12} /> Add Invoice
          </button>
        </div>
      </div>

      {showForm && (
        <div className="p-4 rounded-lg border bg-white" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>New Invoice</h4>
            <button onClick={() => setShowForm(false)}><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>Vendor</label>
              <select value={form.vendor_id || ''} onChange={e => {
                const v = vendors.find(x => x.id === e.target.value);
                setForm({...form, vendor_id: e.target.value, vendor_name: v?.name || '', category: v?.category || 'Other'});
              }} className="w-full px-2 py-1.5 text-sm rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                <option value="">Select vendor...</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            {[
              { key: 'invoice_number', label: 'Invoice #' },
              { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
              { key: 'amount', label: 'Amount', type: 'number' },
              { key: 'outlet', label: 'Outlet' },
              { key: 'notes', label: 'Notes' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>{f.label}</label>
                <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: e.target.value})}
                  className="w-full px-2 py-1.5 text-sm rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: '#2A7B88' }}>Create Invoice</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-[12px]">
          <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Vendor</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Invoice #</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Date</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Due</th>
            <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Amount</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Outlet</th>
            <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Status</th>
            <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Actions</th>
          </tr></thead>
          <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {payables.map(p => {
              const overdue = p.due_date < today && p.status !== 'Paid';
              return (
                <tr key={p.id} className={overdue ? 'bg-red-50/30' : 'hover:bg-[#FAFAF8]'}>
                  <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{p.vendor_name}</td>
                  <td className="px-3 py-2" style={{ color: '#6B7280' }}>{p.invoice_number || '—'}</td>
                  <td className="px-3 py-2" style={{ color: '#6B7280' }}>{p.invoice_date}</td>
                  <td className="px-3 py-2" style={{ color: overdue ? '#DC2626' : '#6B7280' }}>{p.due_date}{overdue && ' !'}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmtCurrency(p.amount)}</td>
                  <td className="px-3 py-2 cursor-pointer hover:text-[#2A7B88] hover:underline" style={{ color: '#6B7280' }}
                    onClick={() => p.outlet && navigate(`/division/me/outlet/${encodeURIComponent(p.outlet)}`)}>{p.outlet}</td>
                  <td className="px-3 py-2">
                    <select value={p.status} onChange={e => updateStatus(p.id, e.target.value)}
                      className="text-[10px] px-1.5 py-0.5 rounded-full border-0" style={{
                        backgroundColor: p.status === 'Paid' ? '#ECFDF5' : p.status === 'Disputed' ? '#FEF2F2' : overdue ? '#FEF2F2' : p.status === 'Approved' ? '#EFF6FF' : '#F7F5F2',
                        color: p.status === 'Paid' ? '#2D8B5E' : p.status === 'Disputed' ? '#DC2626' : overdue ? '#DC2626' : p.status === 'Approved' ? '#2563EB' : '#6B7280'
                      }}>
                      {['Pending','Approved','Paid','Disputed','Overdue'].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => del(p.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
                  </td>
                </tr>
              );
            })}
            {payables.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>No payables yet. Add invoices to start tracking.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgingDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/vendors/aging').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm py-8 text-center" style={{ color: '#9CA3AF' }}>Loading aging data...</p>;

  return (
    <div className="space-y-4" data-testid="aging-dashboard">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Payables Aging</p>
        <p className="text-xs" style={{ color: '#9CA3AF' }}>Total: {fmtCurrency(data.total_amount)} ({data.total_count} invoices)</p>
      </div>

      {data.total_count === 0 ? (
        <div className="text-center py-12">
          <DollarSign size={32} className="mx-auto mb-2" style={{ color: '#E5E1DC' }} />
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No outstanding payables. Add invoices in the Payables tab.</p>
        </div>
      ) : (
        <>
          {/* Aging bar */}
          <div className="rounded-lg overflow-hidden flex h-8">
            {data.buckets.filter(b => b.amount > 0).map((b, i) => (
              <div key={i} style={{ backgroundColor: b.color, width: `${b.pct}%`, minWidth: b.pct > 0 ? '30px' : 0 }}
                className="flex items-center justify-center text-[10px] text-white font-medium" title={`${b.label}: ${fmtCurrency(b.amount)}`}>
                {b.pct >= 10 ? `${b.pct}%` : ''}
              </div>
            ))}
          </div>

          {/* Aging table */}
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
            <table className="w-full text-[12px]">
              <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Aging Bucket</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Amount</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Count</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>% of Total</th>
              </tr></thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {data.buckets.map((b, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: b.color }} />
                      <span className="font-medium" style={{ color: '#1B2A4A' }}>{b.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium" style={{ color: '#1B2A4A' }}>{fmtCurrency(b.amount)}</td>
                    <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{b.count} invoices</td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-semibold" style={{ color: b.color }}>{b.pct}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#F7F5F2' }}>
                  <td className="px-3 py-2 font-bold" style={{ color: '#1B2A4A' }}>Total</td>
                  <td className="px-3 py-2 text-right font-bold" style={{ color: '#1B2A4A' }}>{fmtCurrency(data.total_amount)}</td>
                  <td className="px-3 py-2 text-right font-bold" style={{ color: '#6B7280' }}>{data.total_count}</td>
                  <td className="px-3 py-2 text-right font-bold">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CashFlowView() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get('/vendors/cashflow').then(r => setData(r.data)).catch(() => {});
    api.get('/vendors/alerts').then(r => setAlerts(r.data.alerts || [])).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm py-8 text-center" style={{ color: '#9CA3AF' }}>Loading...</p>;

  const cards = [
    { label: 'Overdue', ...data.overdue, color: '#DC2626', bg: '#FEF2F2' },
    { label: 'Due This Week', ...data.this_week, color: '#D97706', bg: '#FEF3C7' },
    { label: 'Due Next Week', ...data.next_week, color: '#2563EB', bg: '#EFF6FF' },
    { label: 'Due This Month', ...data.this_month, color: '#2D8B5E', bg: '#ECFDF5' },
  ];

  return (
    <div className="space-y-4" data-testid="cashflow-view">
      <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Cash Flow — Outflows</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c, i) => (
          <div key={i} className="rounded-lg p-3 border" style={{ borderColor: '#E5E1DC', backgroundColor: c.bg }}>
            <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: c.color }}>{c.label}</p>
            <p className="text-xl font-bold mt-1" style={{ color: c.color }}>{fmtCurrency(c.amount)}</p>
            <p className="text-[10px]" style={{ color: '#6B7280' }}>{c.count} invoices</p>
          </div>
        ))}
      </div>

      {alerts.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Alerts ({alerts.length})</p>
          {alerts.slice(0, 10).map((a, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg" style={{ backgroundColor: a.type === 'red' ? '#FEF2F2' : '#FEF3C7' }}>
              <AlertTriangle size={14} style={{ color: a.type === 'red' ? '#DC2626' : '#D97706', marginTop: 1 }} />
              <p className="text-[12px]" style={{ color: a.type === 'red' ? '#DC2626' : '#92400E' }}>{a.message}</p>
            </div>
          ))}
        </div>
      )}

      {alerts.length === 0 && data.overdue.count === 0 && (
        <div className="text-center py-8">
          <TrendingUp size={32} className="mx-auto mb-2" style={{ color: '#2D8B5E' }} />
          <p className="text-sm" style={{ color: '#2D8B5E' }}>All clear! No overdue payables.</p>
        </div>
      )}
    </div>
  );
}
